// ContainerStatusPage.tsx -- local-edge view of managed container status.
// Polls GET /api/admin/containers/status every 10 seconds. No tunnel
// required; served by the edge's own api-server via normal login.

import { useState, useCallback } from 'react'
import {
  PageHeader,
  DataTable,
  StatusBadge,
  ConfirmDialog,
  ColumnSettingsButton,
  colors,
  btnPrimary,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { getAuthToken } from '../../services/api'
import { useApiPolling } from '../../hooks/use-api-polling'
import { useAuth } from '../../hooks/use-auth'
import type { ContainerStatus } from '@alpha-edge/shared'
import { useTableColumns } from '../../hooks/use-table-columns'

interface ContainerStatusResponse {
  readonly containers: readonly ContainerStatus[]
  readonly runtime: string
  readonly timestamp: string
}

function containerStatusVariant(status: string): 'active' | 'inactive' | 'warning' {
  if (status === 'running') return 'active'
  if (status === 'paused') return 'warning'
  return 'inactive'
}

function relativeUptime(started: string | null | undefined): string {
  if (!started) return '—'
  const ms = Date.now() - new Date(started).getTime()
  if (ms < 0) return '—'
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ${minutes % 60}m`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

function PctBar({ value }: { readonly value: number | null | undefined }) {
  if (value == null) return <span style={{ color: colors.textMuted }}>—</span>
  const clamped = Math.min(100, Math.max(0, value))
  const color = clamped >= 90 ? '#dc2626' : clamped >= 70 ? '#f59e0b' : '#16a34a'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div
        style={{
          width: '60px',
          height: '8px',
          background: colors.border,
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${clamped}%`, height: '100%', background: color, borderRadius: '4px' }} />
      </div>
      <span style={{ fontSize: '0.8125rem', color: colors.text }}>{value.toFixed(1)}%</span>
    </div>
  )
}

export function ContainerStatusPage() {
  const { user } = useAuth()
  const isMedflowAdmin = user?.role === 'medflow_admin'

  const { data, loading, error, refresh } = useApiPolling<ContainerStatusResponse>({
    path: '/admin/containers/status',
    intervalMs: 10_000,
  })

  const [confirmName, setConfirmName] = useState<string | null>(null)
  const [restartError, setRestartError] = useState<string | null>(null)
  const [restarting, setRestarting] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('container-status')

  const handleRestart = useCallback(
    async (name: string) => {
      setRestarting(true)
      setRestartError(null)
      try {
        const res = await fetch(`/api/admin/containers/${encodeURIComponent(name)}/restart`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        })
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string }
          throw new Error(body.error ?? `Restart failed (${res.status})`)
        }
        // Brief delay then refresh so the status has time to update
        setTimeout(() => {
          void refresh()
        }, 2000)
      } catch (err) {
        setRestartError(err instanceof Error ? err.message : 'Restart failed')
      } finally {
        setRestarting(false)
        setConfirmName(null)
      }
    },
    [refresh],
  )

  const columns: readonly Column<ContainerStatus>[] = [
    {
      key: 'name',
      header: 'Container',
      sortable: true,
      render: (row) => <strong>{row.name}</strong>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={containerStatusVariant(row.status)} label={row.status} />,
    },
    {
      key: 'cpu',
      header: 'CPU',
      render: (row) => <PctBar value={row.cpuPct} />,
    },
    {
      key: 'mem',
      header: 'Memory',
      render: (row) => <PctBar value={row.memPct} />,
    },
    {
      key: 'uptime',
      header: 'Uptime',
      render: (row) => relativeUptime(row.started),
    },
    {
      key: 'image',
      header: 'Image',
      render: (row) => (
        <span style={{ fontSize: '0.8125rem', fontFamily: 'monospace', color: colors.textMuted }}>{row.image}</span>
      ),
    },
    ...(isMedflowAdmin
      ? [
          {
            key: 'actions',
            header: '',
            render: (row: ContainerStatus) => (
              <button
                style={{ ...btnPrimary, padding: '3px 10px', fontSize: '0.8125rem' }}
                disabled={restarting}
                onClick={(e) => {
                  e.stopPropagation()
                  setConfirmName(row.name)
                }}
              >
                Restart
              </button>
            ),
          } as Column<ContainerStatus>,
        ]
      : []),
  ]

  return (
    <div>
      <PageHeader
        title="Containers"
        subtitle={
          data
            ? `Runtime: ${data.runtime} · Last updated: ${new Date(data.timestamp).toLocaleTimeString()}`
            : 'Container runtime status'
        }
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />

      <div style={{ padding: '24px' }}>
        {restartError && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {restartError}
          </div>
        )}

        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        {loading && !data ? (
          <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading container status...</p>
        ) : (
          <DataTable
            columns={columns}
            rows={[...(data?.containers ?? [])]}
            keyField="name"
            emptyMessage="No managed containers found. The container runtime may not be available in this environment."
            {...tableColumnProps}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmName !== null}
        onClose={() => setConfirmName(null)}
        onConfirm={() => {
          if (confirmName !== null) void handleRestart(confirmName)
        }}
        title="Restart container"
        message={`Restart '${confirmName ?? ''}'? The container will be unavailable for a few seconds.`}
        confirmLabel="Restart"
        variant="primary"
        loading={restarting}
      />
    </div>
  )
}
